import { post, get } from '@/utils/Api'
import { isNumeric } from '@/utils/Helpers'
import { markSyncReload } from '@/services/redirectAfterLogin'
import dayjs from 'dayjs'

export const AUTH_UPDATE_STATE = 'AUTH_UPDATE_STATE'
// user data timeout in milliseconds
// user data will be verified (updated) on next route change if expired
const USER_VERIFY_TIMEOUT = 3600 * 1000
const UserStorageKey = 'fpm_front_signed_user'
const ADMIN_ROLES = ['adminRole', 'superAdminRole']

export const MessageTypes = {
  Get: 'get',
  Set: 'set',
  Sync: 'sync',
  SyncSubscribe: 'sync-subscribe'
}

const _userUpdateHelper = ({ commit, dispatch, rootGetters }, user) => {
  commit(AUTH_UPDATE_STATE, { user })
  return dispatch('getLocalStorageWindow', {}, { root: true }).then((w) => {
      const message = { type: MessageTypes.Set, key: UserStorageKey, payload: user }
      w.postMessage(message, `https://${rootGetters.frontDomain}`)
    }, () => {})
}

const _isTokenExpired = (user) => {
  return !user || dayjs().isAfter(dayjs(user.token_expire_at))
}

let userRestorePromiseResolver
let userRestorePromiseRejecter

export default {
  namespaced: true,
  state: {
    isPushSubscribeChecked: false,
    user: null,
  },
  getters: {
    userTitle(state) {
      if (state.user) {
        return state.user.name ? state.user.name : state.user.email
      }
    },
    isUserSigned(state) {
      return Boolean(state.user && isNumeric(state.user.id) && !_isTokenExpired(state.user))
    },
    isUserOnOwnDomain(state, getters, rootState, rootGetters) {
      return state.user && state.user.domain ? rootGetters.workspaceDomain === state.user.domain : undefined
    },
    isUserAdmin(state) {
      return Boolean(state.user && ADMIN_ROLES.includes(state.user.role))
    }
  },
  mutations: {
    [AUTH_UPDATE_STATE](state, payload) {
      for (let fieldName in payload) {
        if (typeof payload === 'object' && Object.hasOwn(payload, fieldName)) {
          state[fieldName] = payload[fieldName]
        }
      }
    }
  },
  actions: {
    async restoreUserBeforeRouting({ getters, dispatch }) {
      const userRestored = await dispatch('userRestore')
      if (!userRestored) return false
      if (!getters.isUserSigned) return dispatch('refreshToken')
      return dispatch('verify')
    },
    login(context, credentials) {
      return post('/auth/login', credentials).then((r) => {
        if (r && r.data.user) {
          return _userUpdateHelper(context, r.data.user)
        }
        return r
      })
    },
    loginIntranet(context, endpoint) {
      return get(endpoint).then((r) => {
        if (r && r.data.user) {
          return _userUpdateHelper(context, r.data.user)
        }
        return r
      })
    },
    loginMs(context, credentials) {
      return post('/oauth/login-ms', credentials).then((r) => {
        if (r && r.data.user) {
          return _userUpdateHelper(context, r.data.user)
        }
        return r
      })
    },
    loginByUser(context, user) {
      return _userUpdateHelper(context, user)
    },
    logout(context) {
      return _userUpdateHelper(context, null)
    },
    register(context, credentials) {
      return post('/auth/workspace-register', credentials)
    },
    registerVerification(context, data) {
      return post('/auth/workspace-register-verification', data)
    },
    passwordResetRequest(context, data) {
      return post('/auth/password-reset-request', data)
    },
    passwordReset(context, data) {
      return post('/auth/password-reset', data)
    },
    /**
     * Check is token still valid.
     * If so will return promise resolved to true.
     * If token outdated attempt to get new auth-token by refresh token and promise resolved to true
     * If refresh fails return false
     * @param context
     */
    refreshToken(context) {
      let user = context.state.user
      if (!_isTokenExpired(user)) {
        return Promise.resolve(true)
      }

      if (user && user.refresh_token) {
        return post('/auth/refresh', { token: context.state.user.refresh_token }).then((r) => {
            if (r && r.data.user) {
              _userUpdateHelper(context, r.data.user)
            }
            return true
          }, () => {
            return false
          })
      } else {
        _userUpdateHelper(context, null)
        return Promise.resolve(false)
      }
    },
    /**
     * Update user info from api.
     * Call on each route update, if user outdated make an api call
     * @param context

     * @param force - force verification without timeout check
     */
    verify(context, force = false) {
      let user = context.state.user
      if (user && user.token) {
        let received = dayjs(user.received_at)
        force = user.permissions ? force : true
        if (force || received.diff(dayjs()) > USER_VERIFY_TIMEOUT) {
          // update only outdated user
          return post('/auth/verify').then((r) => {
            if (r && r.data.user) {
              _userUpdateHelper(context, r.data.user)
            }
            return true
          })
        }
        return Promise.resolve(true)
      } else {
        _userUpdateHelper(context, null)
        return Promise.resolve(false)
      }
    },
    userMessagesHandlerBind(context) {
      window.addEventListener('message', (e) => {
        switch (e.data.type) {
          case MessageTypes.Set:
            context.commit(AUTH_UPDATE_STATE, { user: e.data.payload })
            userRestorePromiseResolver(Boolean(e.data.payload))
            break
          case MessageTypes.Sync:
            context.commit(AUTH_UPDATE_STATE, { user: e.data.payload })
            // mark reload as sync-triggered so handleRoute skips saveRedirectPath
            // and doesn't overwrite the redirect path saved in another tab
            markSyncReload()
            window.location.reload()
        }
      })
    },
    async localStorageUpdateBind(context) {
      const childWindow = await context.dispatch('getLocalStorageWindow', {}, { root: true })
      if (childWindow) {
        childWindow.postMessage({ type: MessageTypes.SyncSubscribe, key: UserStorageKey }, `https://${context.rootGetters.frontDomain}`)
      }
    },
    /**
     * Load user from localStorage and restore it
     * @param context
     */
    async userRestore(context) {
      if (context.state.user) return true

      const restorePromise = new Promise((r, j) => {
        userRestorePromiseResolver = r
        userRestorePromiseRejecter = j
      })

      // start cross-origin syncing
      const childWindow = await context.dispatch('getLocalStorageWindow', {}, { root: true })
      if (childWindow) {
        childWindow.postMessage({ type: MessageTypes.Get, key: UserStorageKey }, `https://${context.rootGetters.frontDomain}`)
      }

      return restorePromise
    }
  }
}
